import { getI18n } from "react-i18next";

/**
 * The one place a language code becomes an Intl locale.
 *
 * Ported from coach web's `src/utilities/locale.ts`, cut to the six languages this app ships.
 * "en" is en-GB and not the runtime default: a Gymkee date reads "2 October 2026" and a time
 * reads "18:30" (DS35), which is what en-GB gives and en-US does not. Formatters are cached
 * because constructing one is expensive and a totals block builds several per render.
 */
const INTL_LOCALE_MAP: Record<string, string> = {
  en: "en-GB",
};

export function toIntlLocale(lang: string): string {
  // The detector hands back a full tag ("fr-FR") while the app's own code is the base one
  // ("fr"), so the base is looked up too: both have to land on the same Gymkee shapes.
  return INTL_LOCALE_MAP[lang] || INTL_LOCALE_MAP[lang.split("-")[0]] || lang;
}

/** The language i18n is currently on, for every formatter in the app. */
export function currentLanguage(): string {
  return getI18n()?.language || "en";
}

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();
const numberFormatterCache = new Map<string, Intl.NumberFormat>();

export function getDateFormatter(
  lang: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${lang}|${JSON.stringify(options)}`;
  let formatter = dateFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(toIntlLocale(lang), options);
    dateFormatterCache.set(key, formatter);
  }
  return formatter;
}

export function getNumberFormatter(
  lang: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = `${lang}|${JSON.stringify(options)}`;
  let formatter = numberFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(toIntlLocale(lang), options);
    numberFormatterCache.set(key, formatter);
  }
  return formatter;
}
